import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { formatDate } from "@/lib/prefs";
import { ChefHat, ArrowLeft, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import SocialShare from "@/components/SocialShare";
import { resolveImageUrl } from "@/components/ImageUpload";
import { AuthorBioCard } from "@/pages/Contributors";
import { useMetaTags } from "@/lib/useMetaTags";
import { coverForPost } from "@/lib/journalCover";
import { chefPortraitFor } from "@/lib/chefAvatar";

/**
 * Inject a single <script type="application/ld+json"> tag into document.head
 * for the lifetime of the component. Cleanly removes on unmount and on data
 * change so Googlebot only ever sees ONE Article schema per post (the current
 * one). Client-side rendering is fine: Google fully executes JS since 2019.
 */
function useJsonLd(id, data) {
  const serialized = data ? JSON.stringify(data) : null;
  useEffect(() => {
    if (!serialized) return;
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    el.text = serialized;
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [id, serialized]);
}

/**
 * Build a schema.org/BlogPosting object for a fetched post. Falls back to the
 * platform's own byline when no chef contributor is attached.
 */
function buildArticleJsonLd(post) {
  if (!post) return null;
  const origin = (typeof window !== "undefined" && window.location.origin) || "https://www.culinairespecialist.com";
  const url = `${origin}/blog/${post.slug}`;
  const image = post.hero_image ? resolveImageUrl(post.hero_image) : coverForPost(post);
  const authorNode = post.contributor
    ? {
        "@type": "Person",
        name: post.contributor.name,
        url: post.contributor.slug ? `${origin}/authors/${post.contributor.slug}` : undefined,
        jobTitle: post.contributor.title || undefined,
        worksFor: post.contributor.restaurant ? { "@type": "Organization", name: post.contributor.restaurant } : undefined,
      }
    : { "@type": "Organization", name: post.author_name || "CulinaireSpecialist" };
  // Strip markdown so the description reads well in a rich result snippet.
  const description = (post.excerpt || (post.body || "").replace(/[#*`_>\[\]\(\)]/g, " ").replace(/\s+/g, " ").trim().slice(0, 200)) || post.title;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description,
    image: [image],
    datePublished: post.published_at || post.created_at,
    dateModified: post.updated_at || post.published_at || post.created_at,
    author: authorNode,
    publisher: {
      "@type": "Organization",
      name: "CulinaireSpecialist",
      logo: {
        "@type": "ImageObject",
        url: `${origin}/logo192.png`,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
    url,
    keywords: (post.tags || []).join(", ") || undefined,
    articleSection: post.tags?.[0] || "Journal",
  };
}

function PublicHeader() {
  return (
    <header className="border-b border-border sticky top-0 z-30 backdrop-blur bg-background/85">
      <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/brand/mark-256.png"
            alt="CulinaireSpecialist"
            className="w-9 h-9 rounded-full object-contain"
            style={{ background: "#0a0806", boxShadow: "0 0 0 1px rgba(232,207,154,0.28)" }}
          />
          <div>
            <div className="font-display text-lg leading-tight">CulinaireSpecialist</div>
            <div className="label-eyebrow text-[0.55rem]">Journal</div>
          </div>
        </Link>
        <nav className="flex items-center gap-4 text-sm">
          <Link to="/blog" className="text-muted-foreground hover:text-foreground">Journal</Link>
          <Link to="/press" className="text-muted-foreground hover:text-foreground" data-testid="nav-public-press">Press</Link>
          <Link to="/login" className="text-muted-foreground hover:gold">Sign in</Link>
        </nav>
      </div>
    </header>
  );
}

export function PublicBlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/public/blog"); setPosts(data); }
      finally { setLoading(false); }
    })();
  }, []);
  const origin = (typeof window !== "undefined" && window.location.origin) || "https://www.culinairespecialist.com";
  useMetaTags({
    title: "The CulinaireSpecialist Journal : Notes from the pass",
    description: "Working-kitchen notes on yield, cost, roster and waste. No fluff, no theory. Read what the numbers actually do on the pass.",
    url: `${origin}/blog`,
    image: `${origin}/logo512.png`,
    type: "website",
  });
  // Blog + ItemList JSON-LD so the /blog index qualifies for structured
  // listings in Google search (helps recent-articles carousels too).
  useJsonLd("blog-list-jsonld", posts.length ? {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "The CulinaireSpecialist Journal",
    url: `${origin}/blog`,
    description: "Notes from working kitchens. Yield, cost, roster, waste. No fluff, no theory: what the numbers do on the pass.",
    blogPost: posts.slice(0, 20).map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `${origin}/blog/${p.slug}`,
      datePublished: p.published_at || p.created_at,
      author: {
        "@type": p.contributor ? "Person" : "Organization",
        name: p.contributor?.name || p.author_name || "The Culinaire Specialist Team",
      },
      image: p.hero_image ? resolveImageUrl(p.hero_image) : coverForPost(p),
    })),
  } : null);
  return (
    <div className="min-h-screen" data-testid="public-blog-list">
      <PublicHeader />
      <main className="max-w-5xl mx-auto px-6 py-16">
        <div className="label-eyebrow mb-4">The CulinaireSpecialist Journal</div>
        <h1 className="font-display text-5xl md:text-6xl leading-none mb-4">Notes from the pass.</h1>
        <p className="text-muted-foreground max-w-2xl mb-16 text-lg leading-relaxed">Notes from working kitchens. Yield, cost, roster, waste. No fluff, no theory: what the numbers do on the pass.</p>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : posts.length === 0 ? (
          <div className="text-muted-foreground text-sm">No articles published yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {posts.map((p) => {
              const coverSrc = p.hero_image ? resolveImageUrl(p.hero_image) : coverForPost(p);
              return (
              <Link key={p.id} to={`/blog/${p.slug}`} className="group card-hover rounded-2xl glass-card overflow-hidden" data-testid={`public-post-${p.slug}`}>
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img
                    src={coverSrc}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                    style={{ transitionDuration: "500ms", filter: "saturate(0.92) contrast(1.05)" }}
                  />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: "linear-gradient(180deg, rgba(10,8,6,0.05) 40%, rgba(10,8,6,0.55) 100%)",
                    }}
                  />
                </div>
                <div className="p-6">
                  <div className="label-eyebrow gold mb-2">{p.tags?.slice(0, 3).join(" • ") || "Article"}</div>
                  <h2 className="font-display text-2xl leading-tight mb-2">{p.title}</h2>
                  {p.excerpt && <p className="text-muted-foreground text-sm line-clamp-3">{p.excerpt}</p>}
                  <div className="mt-4 flex items-center gap-2.5 text-xs text-muted-foreground">
                    {p.contributor ? (
                      <img
                        src={p.contributor.photo_url ? resolveImageUrl(p.contributor.photo_url) : chefPortraitFor(p.contributor)}
                        alt={p.contributor.name}
                        loading="lazy"
                        className="w-7 h-7 rounded-full object-cover shrink-0 ring-1 ring-white/10"
                        style={{ filter: "saturate(0.9) contrast(1.05)" }}
                      />
                    ) : (
                      <img
                        src="/brand/mark-256.png"
                        alt="Culinaire Specialist"
                        loading="lazy"
                        className="w-7 h-7 rounded-full object-contain shrink-0"
                        style={{
                          background: "#0a0806",
                          boxShadow: "0 0 0 1px rgba(212,175,55,0.55), 0 0 12px -4px rgba(212,175,55,0.45)",
                        }}
                      />
                    )}
                    <div className="flex flex-wrap items-center gap-x-1.5">
                      {p.contributor?.slug ? (
                        <span
                          role="link"
                          className="text-foreground hover:gold cursor-pointer"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `/authors/${p.contributor.slug}`; }}
                          data-testid={`byline-${p.slug}`}
                        >
                          {p.contributor.name}
                        </span>
                      ) : (
                        <span className="text-foreground font-medium" data-testid={`byline-${p.slug}`}>
                          {p.contributor?.name || p.author_name || "The Culinaire Specialist Team"}
                        </span>
                      )}
                      <span>•</span>
                      <span>{formatDate(p.published_at || p.created_at)}</span>
                    </div>
                  </div>
                </div>
              </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

export function PublicBlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/public/blog/${slug}`); setPost(data); }
      catch (e) { setError(e.response?.status === 404 ? "Article not found." : "Could not load."); }
      finally { setLoading(false); }
    })();
  }, [slug]);
  // Keep the browser tab + share-preview title in sync with the loaded post.
  const origin = (typeof window !== "undefined" && window.location.origin) || "https://www.culinairespecialist.com";
  const postUrl = post ? `${origin}/blog/${post.slug}` : undefined;
  const postImage = post?.hero_image ? resolveImageUrl(post.hero_image) : (post ? coverForPost(post) : `${origin}/logo512.png`);
  const postDescription = post
    ? (post.excerpt || (post.body || "").replace(/[#*`_>\[\]\(\)]/g, " ").replace(/\s+/g, " ").trim().slice(0, 200))
    : undefined;
  useMetaTags(post ? {
    title: `${post.title} : CulinaireSpecialist Journal`,
    description: postDescription,
    url: postUrl,
    image: postImage,
    type: "article",
  } : {});
  // Inject BlogPosting JSON-LD so chef bylines land as Google rich results.
  useJsonLd("post-article-jsonld", buildArticleJsonLd(post));
  if (loading) return <><PublicHeader /><div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div></>;
  if (error || !post) return (
    <>
      <PublicHeader />
      <div className="max-w-2xl mx-auto p-16 text-center">
        <h1 className="font-display text-3xl mb-4">{error || "Not found"}</h1>
        <Link to="/blog" className="text-sm gold hover:underline">← Back to Journal</Link>
      </div>
    </>
  );
  return (
    <div className="min-h-screen" data-testid="public-blog-post">
      <PublicHeader />
      <article className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/blog" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-8"><ArrowLeft className="w-4 h-4" /> Journal</Link>
        <div className="label-eyebrow gold mb-3">{post.tags?.join(" • ") || "Article"}</div>
        <h1 className="font-display text-4xl md:text-5xl leading-[1.05] mb-4">{post.title}</h1>
        <div className="flex items-center gap-3 text-sm text-muted-foreground mb-8">
          {post.contributor ? (
            <>
              <img
                src={post.contributor.photo_url ? resolveImageUrl(post.contributor.photo_url) : chefPortraitFor(post.contributor)}
                alt={post.contributor.name}
                loading="lazy"
                className="w-11 h-11 rounded-full object-cover shrink-0 ring-1 ring-white/10"
                style={{ filter: "saturate(0.9) contrast(1.05)" }}
              />
              <div className="flex flex-col leading-tight">
                <Link to={`/authors/${post.contributor.slug}`} className="text-foreground hover:gold transition-colors font-medium" data-testid="post-byline-contributor">
                  {post.contributor.name}
                </Link>
                <div className="text-xs opacity-70 mt-0.5">
                  {post.contributor.title && <>{post.contributor.title} <span className="mx-1">•</span></>}
                  {formatDate(post.published_at || post.created_at)}
                </div>
              </div>
            </>
          ) : (
            <>
              <img
                src="/brand/mark-256.png"
                alt="Culinaire Specialist"
                loading="lazy"
                className="w-11 h-11 rounded-full object-contain shrink-0"
                style={{
                  background: "#0a0806",
                  boxShadow: "0 0 0 1px rgba(212,175,55,0.6), 0 6px 20px -6px rgba(212,175,55,0.55)",
                }}
              />
              <div className="flex flex-col leading-tight">
                <span className="text-foreground font-medium" data-testid="post-byline-house">
                  {post.author_name || "The Culinaire Specialist Team"}
                </span>
                <div className="text-xs opacity-70 mt-0.5">
                  Editorial team • {formatDate(post.published_at || post.created_at)}
                </div>
              </div>
            </>
          )}
        </div>
        {post.hero_image ? (
          <img src={resolveImageUrl(post.hero_image)} alt={post.title} className="w-full max-h-[520px] object-cover rounded-lg mb-10" />
        ) : (
          <img src={coverForPost(post)} alt={post.title} loading="lazy" className="w-full max-h-[520px] object-cover rounded-lg mb-10" style={{ filter: "saturate(0.92) contrast(1.05)" }} />
        )}
        {post.excerpt && <p className="font-display text-xl italic text-muted-foreground border-l-2 border-primary/60 pl-5 mb-8">{post.excerpt}</p>}
        <div className="prose prose-invert prose-lg max-w-none prose-headings:font-display prose-a:gold prose-blockquote:border-primary/60 prose-blockquote:text-muted-foreground prose-code:bg-secondary prose-code:px-1.5 prose-code:rounded prose-code:text-primary">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body || ""}</ReactMarkdown>
        </div>
        {post.contributor && <AuthorBioCard contributor={post.contributor} />}
        <div className="luxury-divider my-14" />
        <SocialShare url={`/blog/${post.slug}`} title={post.title} hashtags={["culinaire", "chef", ...(post.tags || [])]} testId="post-share" />
      </article>
    </div>
  );
}
